import React from 'react';
import { useParams } from 'react-router-dom';
import { formatAsMoney, timeSince } from '@perch-labs/warbler';
import { Icon, Link, Text } from '@perch-labs/shrike';
import { copyFor } from 'config';
import { Container } from 'styles';
import { useProductStatus, ProductType } from 'hooks/use-product-status.hook';
import { useUtilityAccount } from 'hooks/use-utility-account.hook';
import { PAST_DUE_ASSISTANCE_AMOUNT } from 'domain/vnm';
import { StyledCard } from './solar-notification.style';

const getCopy = copyFor('solar.notifications');

export const SolarNotification = () => {
  const { planId } = useParams<{ planId?: string }>();
  const { data } = useUtilityAccount({ planId: Number(planId) });
  const vnm = useProductStatus(ProductType.Vnm, Number(planId));

  const pastDue = data?.billPayIneligibleWithLargeBalance;
  const isIneligible = vnm.data?.isIneligible;
  if ((!pastDue && !isIneligible) || !data || !data.pastDueAmount) return null;

  if (pastDue) {
    const { pastDueAmount, pastDueUpdatedAt, utility } = data;
    const { url, displayName, financialAssistanceProgramLink } = utility;

    const shouldShowAssistanceProgramLink =
      (pastDueAmount || 0) > PAST_DUE_ASSISTANCE_AMOUNT &&
      financialAssistanceProgramLink;

    const utilityLink = (
      <Link href={url || undefined} whiteSpace="no-wrap">
        {getCopy('pastDue.link', { displayName })}
      </Link>
    );

    const text = shouldShowAssistanceProgramLink ? (
      <Container flex direction="column">
        <Text margin={{ bottom: '16px' }}>
          {getCopy('pastDue.titleWithPaymentAssistance', {
            displayName,
            pastDueAmount: formatAsMoney(pastDueAmount),
          })}
          <Link
            type="inline"
            href={financialAssistanceProgramLink || undefined}
          >
            {getCopy('pastDue.paymentAssistanceLink')}
          </Link>
        </Text>
        <Text>{utilityLink}</Text>
      </Container>
    ) : (
      <Text>
        {getCopy('pastDue.title', {
          displayName,
          pastDueAmount: formatAsMoney(pastDueAmount),
        })}
        {utilityLink}
      </Text>
    );

    return (
      <StyledCard background="error" margin={{ bottom: '24px' }}>
        <Container flex direction="column">
          <Container flex>
            <div>
              <Icon
                icon="ExclamationCircleInverted"
                color="errorBright"
                margin={{ right: '16px', top: '5px' }}
              />
            </div>
            {text}
          </Container>
          <Text
            opacity="medium"
            textStyle="paragraph400"
            margin={{ left: '32px', top: '8px' }}
          >
            {getCopy('pastDue.subtitle', {
              days: timeSince(pastDueUpdatedAt),
            })}
          </Text>
        </Container>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <Container flex>
        <div>
          <Icon
            icon="ExclamationCircleInverted"
            margin={{ right: '16px', top: '5px' }}
          />
        </div>
        <Text>{getCopy('ineligible.title')}</Text>
      </Container>
    </StyledCard>
  );
};
